import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Alert, Box, Button, Chip, Divider, Menu, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import RoomTypeEditHistoryTabs from '~/components/Vendors/Properties/RoomTypeEditHistory/RoomTypeEditHistoryTabs';

import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';
import { useCreateRoomRate } from '~/hooks/apis/reservation/useCreateRoomRate';
import { useDeleteRoomRate } from '~/hooks/apis/reservation/useDeleteRoomRate';
import type { OfferRatePlan } from '~/hooks/apis/reservation/useOfferRatePlans';

import { getSalesforceBedding, getSalesforceMaxOccupancy, getSalesforceRoomAllocation } from '../../utils';

import RatePlanDetailPanel from './RatePlanDetailPanel';
import RoomRateSubTabsPanel from './RoomRateSubTabsPanel';
import RoomTypeBreadcrumbs from './RoomTypeBreadcrumbs';

function getRoomRateName(roomRate: App.RoomRate) {
  return roomRate.rate_plan.name + (roomRate.rate_plan.rate_plan_code ? ` (${roomRate.rate_plan.rate_plan_code})` : '');
}

function getDefaultRoomRateId(roomType: App.RoomType): string | undefined {
  return (roomType.room_rates?.find((roomRate) => roomRate.rate_plan?.default_plan) ?? roomType.room_rates?.[0])?.id;
}

function getRoomAllocation(roomType: App.RoomType, deals: Array<OpportunityRelatedDeal> | undefined): string {
  const { rawAllocation } = getSalesforceRoomAllocation(roomType.name, deals);
  return rawAllocation ?? 'N/A';
}

interface RoomTypeSummaryPart {
  label: string;
  value: string;
}

function getRoomTypeSummary(
  roomType: App.RoomType,
  deals: Array<OpportunityRelatedDeal> | undefined,
): Array<RoomTypeSummaryPart> {
  return [
    { label: 'Bedding Configuration', value: getSalesforceBedding(roomType.name, deals) ?? 'N/A' },
    { label: 'Max occupancy', value: getSalesforceMaxOccupancy(roomType.name, deals) ?? 'N/A' },
    { label: 'Allocation', value: getRoomAllocation(roomType, deals) },
  ].filter((part): part is RoomTypeSummaryPart => part !== undefined);
}

interface Props {
  propertyId: string;
  vendorId: string;
  channelManaged: boolean;
  roomTypes: Array<App.RoomType>;
  deals: Array<OpportunityRelatedDeal> | undefined;
  keyDates: App.KeyDates | undefined;
  roomTypeId: string;
  roomRateId: string | null;
  viewingRatePlanId: string | null;
  availableRatePlans: Array<OfferRatePlan>;
  onChangeRoom: (roomTypeId: string) => void;
  onChangeRoomRate: (roomRateId: string | undefined) => void;
  onViewRatePlan: (ratePlanId: string) => void;
  onCloseRatePlan: () => void;
  onClose: () => void;
}

export default function RoomRateAvailabilityPanel({
  propertyId,
  vendorId,
  channelManaged,
  roomTypes,
  deals,
  keyDates,
  roomTypeId,
  roomRateId,
  viewingRatePlanId,
  availableRatePlans,
  onChangeRoom,
  onChangeRoomRate,
  onViewRatePlan,
  onCloseRatePlan,
  onClose,
}: Props) {
  const roomType = roomTypes.find((candidate) => candidate.id === roomTypeId);

  const [createMenuAnchor, setCreateMenuAnchor] = useState<HTMLElement | null>(null);

  const showConfirmDialog = useConfirmDialog();
  const { enqueueSnackbar } = useSnackbar();
  const createRoomRate = useCreateRoomRate();
  const deleteRoomRate = useDeleteRoomRate();

  const roomRates = useMemo(() => roomType?.room_rates ?? [], [roomType]);
  const activeRoomRateId = roomRateId ?? (roomType ? getDefaultRoomRateId(roomType) : undefined);
  const activeRoomRate = roomRates.find((roomRate) => roomRate.id === activeRoomRateId) ?? roomRates[0];

  const unlinkedRatePlans = useMemo(() => {
    const linkedRatePlanIds = new Set(roomRates.map((roomRate) => roomRate.rate_plan.id));
    return availableRatePlans.filter((ratePlan) => !linkedRatePlanIds.has(ratePlan.id));
  }, [availableRatePlans, roomRates]);

  function handleCreateRoomRate(ratePlanId: string) {
    setCreateMenuAnchor(null);
    if (!roomType) {
      return;
    }
    createRoomRate.mutate(
      { propertyId, roomTypeId: roomType.id, ratePlanId },
      {
        onSuccess: () => enqueueSnackbar('Room rate created', { variant: 'success' }),
        onError: (error: Error) =>
          enqueueSnackbar(`Failed to create room rate: ${error.message}`, { variant: 'error' }),
      },
    );
  }

  async function handleUnlink(roomRate: App.RoomRate) {
    if (!roomType) {
      return;
    }

    const confirmed = await showConfirmDialog({
      title: `Unlink ${getRoomRateName(roomRate)}?`,
      description: 'Please unlink or re-map packages using this room rate first — this action cannot be undone.',
    });
    if (!confirmed) {
      return;
    }

    deleteRoomRate.mutate(
      { propertyId, roomTypeId: roomType.id, roomRateId: roomRate.id },
      {
        onSuccess: () => {
          enqueueSnackbar('Room rate unlinked', { variant: 'success' });
          if (roomRate.id === activeRoomRateId) {
            const remainingRoomRates = roomRates.filter((candidate) => candidate.id !== roomRate.id);
            const fallbackRoomRateId = getDefaultRoomRateId({ ...roomType, room_rates: remainingRoomRates });
            onChangeRoomRate(fallbackRoomRateId);
          }
        },
        onError: (error: Error) =>
          enqueueSnackbar(`Failed to unlink room rate: ${error.message}`, { variant: 'error' }),
      },
    );
  }

  if (!roomType) {
    return (
      <Box>
        <RoomTypeBreadcrumbs roomTypeName="Room type not found" onNavigateToRoomTypes={onClose} />
        <Alert severity="error">
          This room type could not be found — it may have been removed. Go back to Rates and Inventory and try again.
        </Alert>
      </Box>
    );
  }

  if (viewingRatePlanId) {
    return (
      <RatePlanDetailPanel
        vendorId={vendorId}
        roomTypeName={roomType.name}
        ratePlanId={viewingRatePlanId}
        onBack={onCloseRatePlan}
        onNavigateToRoomTypes={onClose}
      />
    );
  }

  const roomTypeSummary = getRoomTypeSummary(roomType, deals);

  return (
    <Box>
      <RoomTypeBreadcrumbs roomTypeName={roomType.name} onNavigateToRoomTypes={onClose} />

      <Stack spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4">{roomType.name}</Typography>
          <Typography variant="h6" color="text.secondary">
            {roomTypeSummary.map(({ label, value }, index) => (
              <React.Fragment key={label}>
                {index > 0 && ' · '}
                <Box component="span" fontWeight="bold">
                  {label}:
                </Box>{' '}
                {value}
              </React.Fragment>
            ))}
          </Typography>
        </Box>

        <Stack direction="row" spacing={3} alignItems="flex-end">
          <TextField
            select
            label="Change room"
            size="small"
            value={roomType.id}
            onChange={(event) => onChangeRoom(event.target.value)}
            sx={{ width: 320 }}
            SelectProps={{ sx: { '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis' } } }}
          >
            {roomTypes.map((candidate) => (
              <MenuItem key={candidate.id} value={candidate.id}>
                {candidate.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Active room rate"
            size="small"
            value={activeRoomRate?.id ?? ''}
            onChange={(event) => onChangeRoomRate(event.target.value)}
            disabled={roomRates.length === 0}
            sx={{ width: 320 }}
            SelectProps={{ sx: { '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis' } } }}
          >
            {!activeRoomRate && (
              <MenuItem value="">
                <Typography sx={{ fontStyle: 'italic' }}>None</Typography>
              </MenuItem>
            )}
            {roomRates.map((roomRate) => (
              <MenuItem key={roomRate.id} value={roomRate.id}>
                {getRoomRateName(roomRate)}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ flexGrow: 1 }} />

          {activeRoomRate && (
            <Button
              variant="contained"
              size="medium"
              startIcon={<VisibilityOutlinedIcon fontSize="small" />}
              onClick={() => onViewRatePlan(activeRoomRate.rate_plan.id)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              View rate plan
            </Button>
          )}
        </Stack>
      </Stack>

      <Paper variant="outlined">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Typography variant="subtitle1">Rate plans linked to this room</Typography>
          <Button
            variant="contained"
            color="primary"
            size="small"
            endIcon={<ArrowDropDownIcon />}
            disabled={unlinkedRatePlans.length === 0 || createRoomRate.isPending}
            onClick={(event) => setCreateMenuAnchor(event.currentTarget)}
          >
            Create room rate
          </Button>
          <Menu anchorEl={createMenuAnchor} open={!!createMenuAnchor} onClose={() => setCreateMenuAnchor(null)}>
            {unlinkedRatePlans.map((ratePlan) => (
              <MenuItem key={ratePlan.id} onClick={() => handleCreateRoomRate(ratePlan.id)}>
                {ratePlan.name}
              </MenuItem>
            ))}
          </Menu>
        </Stack>

        <Stack divider={<Divider />} sx={{ px: 2 }}>
          {roomRates.map((roomRate) => (
            <Stack
              key={roomRate.id}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ py: 1.5 }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="body2">{getRoomRateName(roomRate)}</Typography>
                {roomRate.id === activeRoomRate?.id && <Chip label="Active" size="small" color="success" />}
                {roomRate.rate_plan.status === 'hidden' && <Chip label="Inactive" size="small" />}
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  size="small"
                  startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                  onClick={() => onViewRatePlan(roomRate.rate_plan.id)}
                >
                  View rate plan
                </Button>
                <Button
                  color="error"
                  size="small"
                  startIcon={<DeleteOutlineIcon fontSize="small" />}
                  onClick={() => handleUnlink(roomRate)}
                  disabled={deleteRoomRate.isPending}
                >
                  Unlink
                </Button>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Paper>

      {activeRoomRate && (
        <Paper variant="outlined" sx={{ mt: 3, p: 3 }}>
          <RoomRateSubTabsPanel
            propertyId={propertyId}
            vendorId={vendorId}
            roomType={roomType}
            roomRate={activeRoomRate}
            propertyRoomTypes={roomTypes}
            channelManaged={channelManaged}
            deals={deals}
            keyDates={keyDates}
          />
        </Paper>
      )}

      {activeRoomRate && (
        <Paper variant="outlined" sx={{ mt: 3, p: 3 }}>
          <PageSubheader title="Availability and surcharge history" />
          <RoomTypeEditHistoryTabs
            propertyId={propertyId}
            roomType={roomType}
            roomRate={activeRoomRate}
            roomRates={roomRates}
            channelManaged={channelManaged}
          />
        </Paper>
      )}
    </Box>
  );
}
